import {
  Email,
  Facebook,
  Instagram,
  LinkedIn,
  LocationOn,
  Phone,
  Twitter,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';

const Footer = () => {
  const theme = useTheme();
  const { t } = useI18n();

  const footerLinks = {
    catalog: [
      { label: t('footer.livingRoom'), path: '/products?room=living_room' },
      { label: t('footer.bedroom'), path: '/products?room=bedroom' },
      { label: t('footer.kitchen'), path: '/products?room=kitchen' },
      { label: t('footer.office'), path: '/products?room=office' },
      { label: t('footer.allProducts'), path: '/products' },
    ],
    company: [
      { label: t('footer.about'), path: '/about' },
      { label: t('footer.delivery'), path: '/delivery' },
      { label: t('footer.warranty'), path: '/warranty' },
      { label: t('footer.contacts'), path: '/contacts' },
      { label: t('footer.b2bCoop'), path: '/b2b/apply' },
      { label: 'Блог', path: '/blog' },
    ],
    support: [
      { label: t('footer.help'), path: '/help' },
      { label: t('footer.returns'), path: '/returns' },
      { label: t('footer.privacy'), path: '/privacy' },
      { label: t('footer.terms'), path: '/terms' },
      { label: t('footer.faq'), path: '/faq' },
    ],
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.grey[900],
        color: theme.palette.common.white,
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Furniture Excellence
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
              {t('footer.businessBlock')}
            </Typography>

            {/* Contact Info */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Phone sx={{ fontSize: 16, mr: 1, color: 'grey.400' }} />
                <Typography variant="body2" color="grey.300">
                  +996 312 123-456
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Email sx={{ fontSize: 16, mr: 1, color: 'grey.400' }} />
                <Typography variant="body2" color="grey.300">
                  info@furniture.kg
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOn sx={{ fontSize: 16, mr: 1, color: 'grey.400' }} />
                <Typography variant="body2" color="grey.300">
                  Бишкек, пр. Чуй, д. 156
                </Typography>
              </Box>
            </Box>

            {/* Social Media */}
            <Box>
              <IconButton
                color="inherit"
                component="a"
                href="https://facebook.com"
                target="_blank"
                aria-label="Facebook"
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: 'grey.400',
                  '&:hover': { color: 'white' },
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                color="inherit"
                component="a"
                href="https://instagram.com"
                target="_blank"
                aria-label="Instagram"
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: 'grey.400',
                  '&:hover': { color: 'white' },
                }}
              >
                <Instagram />
              </IconButton>
              <IconButton
                color="inherit"
                component="a"
                href="https://twitter.com"
                target="_blank"
                aria-label="Twitter"
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: 'grey.400',
                  '&:hover': { color: 'white' },
                }}
              >
                <Twitter />
              </IconButton>
              <IconButton
                color="inherit"
                component="a"
                href="https://linkedin.com"
                target="_blank"
                aria-label="LinkedIn"
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: 'grey.400',
                  '&:hover': { color: 'white' },
                }}
              >
                <LinkedIn />
              </IconButton>
            </Box>
          </Grid>

          {/* Catalog Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('footer.catalog')}
            </Typography>
            <Box component="nav">
              {footerLinks.catalog.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  color="inherit"
                  underline="none"
                  sx={{
                    display: 'block',
                    py: 0.5,
                    color: 'grey.300',
                    '&:hover': {
                      color: 'white',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Company Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('footer.company')}
            </Typography>
            <Box component="nav">
              {footerLinks.company.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  color="inherit"
                  underline="none"
                  sx={{
                    display: 'block',
                    py: 0.5,
                    color: 'grey.300',
                    '&:hover': {
                      color: 'white',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Support Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('footer.support')}
            </Typography>
            <Box component="nav">
              {footerLinks.support.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  color="inherit"
                  underline="none"
                  sx={{
                    display: 'block',
                    py: 0.5,
                    color: 'grey.300',
                    '&:hover': {
                      color: 'white',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Divider sx={{ my: 4, borderColor: 'grey.700' }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="grey.400">
              {t('footer.rights')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <Typography variant="body2" color="grey.400" sx={{ mr: 2 }}>
                {t('footer.hours')}
              </Typography>
              <Typography variant="body2" color="grey.400">
                {t('footer.noDaysOff')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* B2B Notice */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="grey.300" align="center">
            {t('footer.b2bPitch')}{' '}
            <Link
              component={RouterLink}
              to="/b2b/apply"
              sx={{ color: 'primary.light', ml: 1 }}
            >
              {t('footer.b2bApply')}
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
